import * as THREE from 'three';
import type { BoxCollider } from './types';
import type { CloudSpawnRecord } from './cloudSpawn';
import { grainRowSpans } from './spawn';
import { createWindowRingAllocator, slotRuns, type WindowRingAllocator } from './windowRing';
import {
  CLOUD_RENDER_FRAG,
  CLOUD_RENDER_VERT,
  cloudPosFragSource,
  cloudVelFragSource,
} from './cloudShaders';
import { SIM_VERT } from './shaders';
import { makeFloatTarget, uploadSpans } from './gpuUtils';
import type { ArenaXZ } from './types';

export interface CloudSim {
  readonly capacity: number;
  readonly texSize: number;
  readonly mesh: THREE.InstancedMesh;
  readonly posTexture: () => THREE.Texture;
  readonly velTexture: () => THREE.Texture;
  readonly metaTexture: () => THREE.Texture;
  readonly originTexture: () => THREE.Texture;
  enqueue(records: CloudSpawnRecord[], simTime?: number): number;
  pendingCount(): number;
  unexpiredCount(now: number): number;
  readMeanVelocity(renderer: THREE.WebGLRenderer): { x: number; y: number; z: number; count: number };
  readOpacity(renderer: THREE.WebGLRenderer): { meanAlpha: number; meanLife: number; count: number };
  readLive(renderer: THREE.WebGLRenderer): Array<{ x: number; y: number; z: number; vx: number; vy: number; vz: number }>;
  step(
    renderer: THREE.WebGLRenderer,
    dt: number,
    heightTex: THREE.Texture,
    baseTex?: THREE.Texture | null,
    camera?: THREE.Camera | null,
    liveCount?: number,
  ): void;
  reset(renderer: THREE.WebGLRenderer): void;
  killAll(renderer: THREE.WebGLRenderer): void;
  setColliders(boxes: BoxCollider[]): void;
  setVisible(on: boolean): void;
  setSixWay(on: boolean): void;
  getSixWay(): boolean;
  setLightDir(x: number, y: number, z: number): void;
  dispose(): void;
}

function writeFrameDefault(
  target: THREE.WebGLRenderTarget,
  size: number,
  renderer: THREE.WebGLRenderer,
): void {
  const data = new Float32Array(size * size * 4);
  for (let i = 0; i < size * size; i += 1) {
    data[i * 4 + 1] = 1;
    data[i * 4 + 3] = 1;
  }
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const props = renderer.properties.get(target.texture) as { __webglTexture?: WebGLTexture } | undefined;
  const tex = props?.__webglTexture ?? null;
  if (tex) {
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, size, size, gl.RGBA, gl.FLOAT, data);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
  }
  renderer.setRenderTarget(prev);
}

function writeDead(target: THREE.WebGLRenderTarget, size: number, renderer: THREE.WebGLRenderer): void {
  const data = new Float32Array(size * size * 4);
  for (let i = 0; i < size * size; i += 1) {
    data[i * 4 + 1] = -100;
    data[i * 4 + 3] = -1;
  }
  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const props = renderer.properties.get(target.texture) as { __webglTexture?: WebGLTexture } | undefined;
  const tex = props?.__webglTexture ?? null;
  if (tex) {
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, size, size, gl.RGBA, gl.FLOAT, data);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
  }
  renderer.setRenderTarget(prev);
}

function fillBoxUniforms(
  centers: THREE.Vector3[],
  halves: THREE.Vector3[],
  boxes: BoxCollider[],
): number {
  const n = Math.min(16, boxes.length);
  for (let i = 0; i < 16; i += 1) {
    if (i < n) {
      const b = boxes[i];
      centers[i].set(b.center[0], b.center[1], b.center[2]);
      halves[i].set(b.half[0], b.half[1], b.half[2]);
    } else {
      centers[i].set(0, -100, 0);
      halves[i].set(0, 0, 0);
    }
  }
  return n;
}

export function createCloudSim(
  renderer: THREE.WebGLRenderer,
  capacity: number,
  arena: ArenaXZ,
  boxes: BoxCollider[],
): CloudSim {
  const texSize = Math.max(1, Math.ceil(Math.sqrt(capacity)));
  const posA = makeFloatTarget(texSize, THREE.NearestFilter);
  const posB = makeFloatTarget(texSize, THREE.NearestFilter);
  const velA = makeFloatTarget(texSize, THREE.NearestFilter);
  const velB = makeFloatTarget(texSize, THREE.NearestFilter);
  const meta = makeFloatTarget(texSize, THREE.NearestFilter);
  const origin = makeFloatTarget(texSize, THREE.NearestFilter);
  const color = makeFloatTarget(texSize, THREE.NearestFilter);
  const frame = makeFloatTarget(texSize, THREE.NearestFilter);

  const prev = renderer.getRenderTarget();
  renderer.setRenderTarget(posA);
  renderer.setRenderTarget(posB);
  renderer.setRenderTarget(velA);
  renderer.setRenderTarget(velB);
  renderer.setRenderTarget(meta);
  renderer.setRenderTarget(origin);
  renderer.setRenderTarget(color);
  renderer.setRenderTarget(frame);
  renderer.setRenderTarget(prev);
  writeDead(posA, texSize, renderer);
  writeDead(posB, texSize, renderer);
  writeDead(velA, texSize, renderer);
  writeDead(velB, texSize, renderer);
  writeFrameDefault(frame, texSize, renderer);

  const dummyBase = new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType);
  dummyBase.needsUpdate = true;
  dummyBase.name = 'particle-lab-cloud-base-dummy';

  const dummySix = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
  dummySix.needsUpdate = true;
  dummySix.name = 'particle-lab-sixway-dummy';

  const sixA = { tex: dummySix as THREE.Texture };
  const sixB = { tex: dummySix as THREE.Texture };
  const sixA2 = { tex: dummySix as THREE.Texture };
  const sixB2 = { tex: dummySix as THREE.Texture };
  const loader = new THREE.TextureLoader();
  const bindSix = () => {
    renderUniforms.tSixA.value = sixA.tex;
    renderUniforms.tSixB.value = sixB.tex;
    renderUniforms.tSixA2.value = sixA2.tex;
    renderUniforms.tSixB2.value = sixB2.tex;
  };
  const loadSix = (url: string, slot: { tex: THREE.Texture }) => {
    loader.load(url, (tex) => {
      tex.flipY = false;
      tex.colorSpace = THREE.NoColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.generateMipmaps = false;
      tex.name = url;
      slot.tex = tex;
      bindSix();
    });
  };
  loadSix('/textures/smoke-6way-a.png', sixA);
  loadSix('/textures/smoke-6way-b.png', sixB);
  loadSix('/textures/smoke-6way-a2.png', sixA2);
  loadSix('/textures/smoke-6way-b2.png', sixB2);

  const boxCenters = Array.from({ length: 16 }, () => new THREE.Vector3());
  const boxHalves = Array.from({ length: 16 }, () => new THREE.Vector3());
  let boxCount = fillBoxUniforms(boxCenters, boxHalves, boxes);

  const simUniforms = {
    tPos: { value: posA.texture as THREE.Texture },
    tVel: { value: velA.texture as THREE.Texture },
    tMeta: { value: meta.texture as THREE.Texture },
    tOrigin: { value: origin.texture as THREE.Texture },
    tColor: { value: color.texture as THREE.Texture },
    tFrame: { value: frame.texture as THREE.Texture },
    tHeight: { value: null as THREE.Texture | null },
    tBase: { value: null as THREE.Texture | null },
    uDt: { value: 1 / 60 },
    uBoxCenter: { value: boxCenters },
    uBoxHalf: { value: boxHalves },
    uBoxCount: { value: boxCount },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
  };

  const posMat = new THREE.ShaderMaterial({
    uniforms: simUniforms,
    vertexShader: SIM_VERT,
    fragmentShader: cloudPosFragSource(),
    depthTest: false,
    depthWrite: false,
  });
  const velMat = new THREE.ShaderMaterial({
    uniforms: { ...simUniforms },
    vertexShader: SIM_VERT,
    fragmentShader: cloudVelFragSource(),
    depthTest: false,
    depthWrite: false,
  });
  const simScene = new THREE.Scene();
  const simCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const simMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), posMat);
  simScene.add(simMesh);

  const camMatrix = new THREE.Matrix4();
  const renderUniforms = {
    tPos: { value: posA.texture as THREE.Texture },
    tVel: { value: velA.texture as THREE.Texture },
    tMeta: { value: meta.texture as THREE.Texture },
    tColor: { value: color.texture as THREE.Texture },
    tHeight: { value: null as THREE.Texture | null },
    tBase: { value: dummyBase as THREE.Texture },
    uTexSize: { value: texSize },
    uLightDir: { value: new THREE.Vector3(6, 10, 4).normalize() },
    uLightColor: { value: new THREE.Color(0xfff1d6).multiplyScalar(1.22) },
    uHemiSky: { value: new THREE.Color(0xb8c0cc).multiplyScalar(0.48) },
    uHemiGround: { value: new THREE.Color(0x1a1816).multiplyScalar(0.50) },
    uCameraMatrixWorld: { value: camMatrix },
    tSixA: { value: dummySix as THREE.Texture },
    tSixB: { value: dummySix as THREE.Texture },
    tSixA2: { value: dummySix as THREE.Texture },
    tSixB2: { value: dummySix as THREE.Texture },
    uSixWay: { value: 1 },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
    uBoxCenter: { value: boxCenters },
    uBoxHalf: { value: boxHalves },
    uBoxCount: { value: boxCount },
  };
  const renderMat = new THREE.ShaderMaterial({
    uniforms: renderUniforms,
    vertexShader: CLOUD_RENDER_VERT,
    fragmentShader: CLOUD_RENDER_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.OneFactor,
    blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
    side: THREE.DoubleSide,
  });
  renderMat.toneMapped = false;
  const geometry = new THREE.PlaneGeometry(1, 1);
  const instanceIds = new Float32Array(capacity);
  for (let i = 0; i < capacity; i += 1) instanceIds[i] = i;
  geometry.setAttribute('aId', new THREE.InstancedBufferAttribute(instanceIds, 1));
  const mesh = new THREE.InstancedMesh(geometry, renderMat, capacity);
  mesh.frustumCulled = false;
  mesh.renderOrder = 10;
  mesh.raycast = () => {};
  mesh.count = capacity;
  mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
  const dummy = new THREE.Object3D();
  dummy.matrix.identity();
  for (let i = 0; i < capacity; i += 1) mesh.setMatrixAt(i, dummy.matrix);
  mesh.instanceMatrix.needsUpdate = true;
  mesh.name = 'particle-lab-fines';

  let readPos = posA;
  let writePos = posB;
  let readVel = velA;
  let writeVel = velB;
  const allocator: WindowRingAllocator = createWindowRingAllocator(capacity);
  const pending: Array<{ rec: CloudSpawnRecord; slot: number }> = [];
  let disposed = false;
  let sixWayOn = true;

  const flushSpawns = (gl: THREE.WebGLRenderer) => {
    if (pending.length === 0) return;
    pending.sort((a, b) => a.slot - b.slot);
    const n = pending.length;
    const posData = new Float32Array(n * 4);
    const velData = new Float32Array(n * 4);
    const metaData = new Float32Array(n * 4);
    const originData = new Float32Array(n * 4);
    const colorData = new Float32Array(n * 4);
    const frameData = new Float32Array(n * 4);
    const slots = new Array<number>(n);
    for (let i = 0; i < n; i += 1) {
      const p = pending[i].rec;
      slots[i] = pending[i].slot;
      posData[i * 4] = p.x;
      posData[i * 4 + 1] = p.y;
      posData[i * 4 + 2] = p.z;
      posData[i * 4 + 3] = 0;
      velData[i * 4] = p.vx;
      velData[i * 4 + 1] = p.vy;
      velData[i * 4 + 2] = p.vz;
      velData[i * 4 + 3] = p.linger;
      metaData[i * 4] = p.size;
      metaData[i * 4 + 1] = p.seed;
      metaData[i * 4 + 2] = p.kind;
      metaData[i * 4 + 3] = p.depositMass;
      originData[i * 4] = p.ox;
      originData[i * 4 + 1] = p.oy;
      originData[i * 4 + 2] = p.oz;
      originData[i * 4 + 3] = p.energy;
      colorData[i * 4] = p.r;
      colorData[i * 4 + 1] = p.g;
      colorData[i * 4 + 2] = p.b;
      colorData[i * 4 + 3] = p.buoyancy;
      frameData[i * 4] = p.nx;
      frameData[i * 4 + 1] = p.ny;
      frameData[i * 4 + 2] = p.nz;
      frameData[i * 4 + 3] = p.upFactor;
    }
    const writeRun = (offset: number, start: number, count: number) => {
      const spans = grainRowSpans(start, count, texSize);
      const slice4 = (src: Float32Array) => src.subarray(offset * 4, (offset + count) * 4);
      uploadSpans(gl, readPos, slice4(posData), spans);
      uploadSpans(gl, writePos, slice4(posData), spans);
      uploadSpans(gl, readVel, slice4(velData), spans);
      uploadSpans(gl, writeVel, slice4(velData), spans);
      uploadSpans(gl, meta, slice4(metaData), spans);
      uploadSpans(gl, origin, slice4(originData), spans);
      uploadSpans(gl, color, slice4(colorData), spans);
      uploadSpans(gl, frame, slice4(frameData), spans);
    };
    const runs = slotRuns(slots);
    for (const run of runs) writeRun(run.offset, run.start, run.count);
    pending.length = 0;
  };

  const bindSim = (heightTex: THREE.Texture, baseTex: THREE.Texture | null | undefined, dt: number) => {
    const base = baseTex ?? dummyBase;
    simUniforms.tPos.value = readPos.texture;
    simUniforms.tVel.value = readVel.texture;
    simUniforms.tMeta.value = meta.texture;
    simUniforms.tOrigin.value = origin.texture;
    simUniforms.tColor.value = color.texture;
    simUniforms.tFrame.value = frame.texture;
    simUniforms.tHeight.value = heightTex;
    simUniforms.tBase.value = base;
    simUniforms.uDt.value = dt;
    simUniforms.uBoxCount.value = boxCount;
    velMat.uniforms.tPos.value = readPos.texture;
    velMat.uniforms.tVel.value = readVel.texture;
    velMat.uniforms.tMeta.value = meta.texture;
    velMat.uniforms.tOrigin.value = origin.texture;
    velMat.uniforms.tColor.value = color.texture;
    velMat.uniforms.tFrame.value = frame.texture;
    velMat.uniforms.tHeight.value = heightTex;
    velMat.uniforms.tBase.value = base;
    velMat.uniforms.uDt.value = dt;
    velMat.uniforms.uBoxCount.value = boxCount;
  };

  return {
    capacity,
    texSize,
    mesh,
    posTexture: () => readPos.texture,
    velTexture: () => readVel.texture,
    metaTexture: () => meta.texture,
    originTexture: () => origin.texture,
    enqueue(records, simTime = 0) {
      const lives = new Array<number>(records.length);
      for (let i = 0; i < records.length; i += 1) lives[i] = records[i].linger;
      const slots = allocator.allocateWithLives(lives, simTime);
      for (let i = 0; i < slots.length; i += 1) {
        pending.push({ rec: records[i], slot: slots[i] });
      }
      return slots.length;
    },
    pendingCount() {
      return pending.length;
    },
    unexpiredCount(now) {
      return allocator.unexpiredCount(now);
    },
    readMeanVelocity(gl) {
      const n = texSize * texSize;
      const pos = new Float32Array(n * 4);
      const vel = new Float32Array(n * 4);
      const prevTarget = gl.getRenderTarget();
      gl.readRenderTargetPixels(readPos, 0, 0, texSize, texSize, pos);
      gl.readRenderTargetPixels(readVel, 0, 0, texSize, texSize, vel);
      gl.setRenderTarget(prevTarget);
      let sx = 0;
      let sy = 0;
      let sz = 0;
      let count = 0;
      for (let i = 0; i < n; i += 1) {
        if (pos[i * 4 + 3] < 0) continue;
        sx += vel[i * 4];
        sy += vel[i * 4 + 1];
        sz += vel[i * 4 + 2];
        count += 1;
      }
      if (count === 0) return { x: 0, y: 0, z: 0, count: 0 };
      return { x: sx / count, y: sy / count, z: sz / count, count };
    },
    readLive(gl) {
      const n = texSize * texSize;
      const pos = new Float32Array(n * 4);
      const vel = new Float32Array(n * 4);
      const prevTarget = gl.getRenderTarget();
      gl.readRenderTargetPixels(readPos, 0, 0, texSize, texSize, pos);
      gl.readRenderTargetPixels(readVel, 0, 0, texSize, texSize, vel);
      gl.setRenderTarget(prevTarget);
      const out: Array<{ x: number; y: number; z: number; vx: number; vy: number; vz: number }> = [];
      for (let i = 0; i < n; i += 1) {
        if (pos[i * 4 + 3] < 0) continue;
        out.push({
          x: pos[i * 4],
          y: pos[i * 4 + 1],
          z: pos[i * 4 + 2],
          vx: vel[i * 4],
          vy: vel[i * 4 + 1],
          vz: vel[i * 4 + 2],
        });
      }
      return out;
    },
    readOpacity(gl) {
      const n = texSize * texSize;
      const pos = new Float32Array(n * 4);
      const vel = new Float32Array(n * 4);
      const prevTarget = gl.getRenderTarget();
      gl.readRenderTargetPixels(readPos, 0, 0, texSize, texSize, pos);
      gl.readRenderTargetPixels(readVel, 0, 0, texSize, texSize, vel);
      gl.setRenderTarget(prevTarget);
      let alphaSum = 0;
      let lifeSum = 0;
      let count = 0;
      for (let i = 0; i < n; i += 1) {
        const age = pos[i * 4 + 3];
        if (age < 0) continue;
        const linger = Math.max(vel[i * 4 + 3], 0.2);
        const t = Math.min(1, Math.max(0, age / linger));
        const fadePow = linger > 4 ? 1.05 : 2.45;
        const fade = (1 - t) ** fadePow;
        const thin = 1 / (1 + 0.85 * t);
        const haze = linger > 4 ? 0.16 : 0.34;
        alphaSum += fade * thin * haze;
        lifeSum += t;
        count += 1;
      }
      if (count === 0) return { meanAlpha: 0, meanLife: 0, count: 0 };
      return { meanAlpha: alphaSum / count, meanLife: lifeSum / count, count };
    },
    step(gl, dt, heightTex, baseTex, camera, _liveCount = 0) {
      if (disposed) return;
      const clamped = Math.min(Math.max(dt, 0), 0.05);
      bindSim(heightTex, baseTex, clamped);
      const prevTarget = gl.getRenderTarget();
      const prevAuto = gl.autoClear;
      gl.autoClear = true;
      simMesh.material = posMat;
      gl.setRenderTarget(writePos);
      gl.render(simScene, simCam);
      simMesh.material = velMat;
      gl.setRenderTarget(writeVel);
      gl.render(simScene, simCam);
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
      const tmpPos = readPos;
      readPos = writePos;
      writePos = tmpPos;
      const tmpVel = readVel;
      readVel = writeVel;
      writeVel = tmpVel;
      flushSpawns(gl);
      allocator.beginStep();
      renderUniforms.tPos.value = readPos.texture;
      renderUniforms.tVel.value = readVel.texture;
      renderUniforms.tHeight.value = heightTex;
      renderUniforms.tBase.value = baseTex ?? dummyBase;
      renderUniforms.uBoxCount.value = boxCount;
      if (camera) {
        camera.updateMatrixWorld();
        camMatrix.copy(camera.matrixWorld);
      }
    },
    reset(gl) {
      pending.length = 0;
      allocator.reset();
      writeDead(posA, texSize, gl);
      writeDead(posB, texSize, gl);
      writeDead(velA, texSize, gl);
      writeDead(velB, texSize, gl);
      writeFrameDefault(frame, texSize, gl);
    },
    killAll(gl) {
      pending.length = 0;
      allocator.reset();
      writeDead(posA, texSize, gl);
      writeDead(posB, texSize, gl);
      writeDead(velA, texSize, gl);
      writeDead(velB, texSize, gl);
      writeFrameDefault(frame, texSize, gl);
    },
    setColliders(next) {
      boxCount = fillBoxUniforms(boxCenters, boxHalves, next);
    },
    setVisible(on) {
      mesh.visible = on;
    },
    setSixWay(on) {
      sixWayOn = on;
      renderUniforms.uSixWay.value = on ? 1 : 0;
    },
    getSixWay() {
      return sixWayOn;
    },
    setLightDir(x, y, z) {
      renderUniforms.uLightDir.value.set(x, y, z).normalize();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      posA.dispose();
      posB.dispose();
      velA.dispose();
      velB.dispose();
      meta.dispose();
      origin.dispose();
      color.dispose();
      frame.dispose();
      posMat.dispose();
      velMat.dispose();
      renderMat.dispose();
      geometry.dispose();
      simMesh.geometry.dispose();
      dummyBase.dispose();
      dummySix.dispose();
    },
  };
}
